import { clamp, isMobileBreakpoint, onDocumentReady, prefersReducedMotion } from '@theme/utilities';

const SELECTOR = '.custom-positioned-container--parallax[data-cpc-parallax="true"]';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class PositionedContainerParallaxManager {
  constructor() {
    this.instances = new Map();
    this.activeInstances = new Set();
    this.frameId = 0;
    this.started = false;
    this.reducedMotionMedia = matchMedia(REDUCED_MOTION_QUERY);
    this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
      rootMargin: '20% 0px 20% 0px',
    });
  }

  init = () => {
    if (this.started) {
      return;
    }

    this.started = true;

    window.addEventListener('scroll', this.scheduleUpdate, { passive: true });
    window.addEventListener('resize', this.scheduleUpdate, { passive: true });
    document.addEventListener('shopify:section:load', this.refresh);
    document.addEventListener('shopify:section:unload', this.refresh);
    document.addEventListener('shopify:block:select', this.scheduleUpdate);
    if (typeof this.reducedMotionMedia.addEventListener === 'function') {
      this.reducedMotionMedia.addEventListener('change', this.refresh);
    } else if (typeof this.reducedMotionMedia.addListener === 'function') {
      this.reducedMotionMedia.addListener(this.refresh);
    }

    this.refresh();
  };

  refresh = () => {
    if (prefersReducedMotion()) {
      this.resetAll();
      return;
    }

    const nextInstances = new Map();

    document.querySelectorAll(SELECTOR).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      const existing = this.instances.get(element);
      const instance = existing ?? {
        element,
        direction: 1,
        strength: 0,
        mobileStrength: 0,
        mobileEnabled: false,
        rotationEnabled: false,
        rotationDirection: 1,
        rotationStrength: 0,
        rotationMobileStrength: 0,
        offset: 0,
        rotation: 0,
      };

      instance.direction = element.dataset.cpcParallaxDirection === 'down' ? 1 : -1;
      instance.strength = Number.parseFloat(element.dataset.cpcParallaxStrength ?? '0') || 0;
      instance.mobileStrength = Number.parseFloat(element.dataset.cpcParallaxMobileStrength ?? '0') || 0;
      instance.mobileEnabled = element.dataset.cpcParallaxMobile === 'true';
      instance.rotationEnabled = element.dataset.cpcParallaxRotation === 'true';
      instance.rotationDirection = element.dataset.cpcParallaxRotationDirection === 'counterclockwise' ? -1 : 1;
      instance.rotationStrength = Number.parseFloat(element.dataset.cpcParallaxRotationStrength ?? '0') || 0;
      instance.rotationMobileStrength =
        Number.parseFloat(element.dataset.cpcParallaxMobileRotationStrength ?? '0') || 0;

      nextInstances.set(element, instance);
    });

    this.instances.forEach((instance, element) => {
      if (nextInstances.has(element)) return;
      this.intersectionObserver.unobserve(element);
      this.activeInstances.delete(instance);
      this.resetInstance(instance);
    });

    this.instances = nextInstances;

    this.instances.forEach((instance, element) => {
      this.intersectionObserver.observe(element);
      this.activeInstances.delete(instance);
      instance.offset = Number.NaN;
      instance.rotation = Number.NaN;
    });

    this.scheduleUpdate();
  };

  handleIntersection = (entries) => {
    entries.forEach((entry) => {
      const instance = this.instances.get(/** @type {HTMLElement} */ (entry.target));
      if (!instance) return;

      if (entry.isIntersecting) {
        this.activeInstances.add(instance);
      } else {
        this.activeInstances.delete(instance);
        this.resetInstance(instance);
      }
    });

    this.scheduleUpdate();
  };

  scheduleUpdate = () => {
    if (this.frameId || prefersReducedMotion()) return;

    this.frameId = window.requestAnimationFrame(() => {
      this.frameId = 0;
      this.update();
    });
  };

  update() {
    if (!this.instances.size) return;

    const viewportHeight = window.innerHeight || 1;
    const isMobile = isMobileBreakpoint();

    this.instances.forEach((instance) => {
      if (!instance.element.isConnected) {
        this.refresh();
        return;
      }

      if (!this.activeInstances.has(instance)) {
        this.resetInstance(instance);
        return;
      }

      const strength = isMobile ? (instance.mobileEnabled ? instance.mobileStrength : 0) : instance.strength;
      const rotationStrength = isMobile
        ? (instance.mobileEnabled ? instance.rotationMobileStrength : 0)
        : instance.rotationStrength;

      if (!strength && !(instance.rotationEnabled && rotationStrength)) {
        this.resetInstance(instance);
        return;
      }

      const rect = instance.element.getBoundingClientRect();
      const viewportCenter = viewportHeight / 2;
      const elementCenter = rect.top + rect.height / 2;
      const distance = (viewportCenter - elementCenter) / Math.max((viewportHeight + rect.height) / 2, 1);
      const offset = clamp(distance, -1, 1) * strength * instance.direction;
      const rotation = instance.rotationEnabled ? clamp(distance, -1, 1) * rotationStrength * instance.rotationDirection : 0;
      const roundedOffset = Math.round(offset * 100) / 100;
      const roundedRotation = Math.round(rotation * 100) / 100;

      if (roundedOffset === instance.offset && roundedRotation === instance.rotation) return;

      instance.offset = roundedOffset;
      instance.rotation = roundedRotation;
      instance.element.style.setProperty('--cpc-parallax-offset', `${roundedOffset}px`);
      instance.element.style.setProperty('--cpc-parallax-rotation', `${roundedRotation}deg`);
      instance.element.style.willChange = 'transform';
    });
  }

  resetInstance(instance) {
    if (instance.offset === 0 && instance.rotation === 0) return;

    instance.offset = 0;
    instance.rotation = 0;
    instance.element.style.setProperty('--cpc-parallax-offset', '0px');
    instance.element.style.setProperty('--cpc-parallax-rotation', '0deg');
    instance.element.style.removeProperty('will-change');
  }

  resetAll() {
    if (this.frameId) {
      window.cancelAnimationFrame(this.frameId);
      this.frameId = 0;
    }

    this.activeInstances.clear();
    this.instances.forEach((instance) => this.resetInstance(instance));
  }
}

onDocumentReady(() => {
  const manager = new PositionedContainerParallaxManager();
  manager.init();
});
